import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { CustomLoader } from '../../../shared';
import { useIsWebView, useToken } from '../../../shared/hooks';
import { useWebViewLoading } from '../../../shared/hooks/useWebViewLoading';
import { appBridge, postMessageToFlutter } from '../../../shared/services/bridge';
import { inquiryRepository } from '../../inquiry/data/inquiryRepository';
import type { PaymentOption } from '../types';
import { buildReceiptPath } from '../utils/postPaymentNavigate';

import './PaymentPending.css';
import './PaymentCommon.css';

/**
 * 決済方法に応じた待機メッセージを取得
 *
 * 待機するのは現金（ドクターの受領確認待ち）のみ。クレジットカードは GMO から
 * /success に戻るため本画面を通らず、領収書はカルテ側で発行する（課題表381）。
 */
const getWaitingMessage = (paymentMethod: PaymentOption) => {
  switch (paymentMethod) {
    case 'cash':
      return {
        title: '支払い手続き中',
        message: '作業員が現金を受領するまで\nお待ちください',
      };
    default:
      return {
        title: '手続き中',
        message: 'しばらくお待ちください',
      };
  }
};

/**
 * 支払い待機画面
 *
 * 決済方法により挙動が変わる:
 * - 現金: ドクターが現金を受領し作業完了になるまで待機。「更新する」で作業完了を
 *   検知したら領収書発行画面（/inquiries/:id/receipt）へ遷移する（課題表381）
 * - 請求書払い: createPayment 時点で作業完了のため即完了表示 + paymentCompleted 送信
 */
const PaymentPending: React.FC = () => {
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const token = useToken();
  const isWebView = useIsWebView();
  // 請求書払いは createPayment 時点で API が作業完了(71)に遷移済みのため、
  // 待機せず初期表示から完了扱いにする（ローディング・更新ボタンを出さない）。
  const isBillPayment = searchParams.get('method') === 'bill';
  // 現金は作業完了を検知したら領収書発行画面へ遷移するため、本画面で完了表示になるのは請求書払いのみ
  const isPaymentComplete = isBillPayment;
  const [isRefreshing, setIsRefreshing] = useState(false);

  // データ取得なし（即座に表示可能）→ isLoading=false でhideLoadingを即時発火
  const contentRef = useWebViewLoading(false);

  // inquiryIdの取得: パスパラメータ優先、なければクエリパラメータ
  const queryInquiryId = searchParams.get('inquiryId');
  const id = pathId || queryInquiryId || '';

  // URLクエリパラメータから決済方法を取得（未指定は現金扱い。API の createPaymentWaitUrl は method を付けない）
  const methodFromUrl = searchParams.get('method') as PaymentOption | null;
  const currentPaymentMethod = methodFromUrl || 'cash';
  const { title, message } = getWaitingMessage(currentPaymentMethod);

  // Web 直接アクセスで token をクエリで持ち回っている場合は遷移先にも引き継ぐ（PaymentSuccess と同じ）
  const tokenParam = searchParams.get('token');

  // 更新ボタンをタップした時にAPIからステータスを確認
  // 作業完了（ドクターが現金を受領）なら領収書発行画面へ進む。
  // 完了表示・paymentCompleted 送信は領収書画面の後の決済完了画面が担う。
  const handleRefresh = async () => {
    if (!id) return;
    setIsRefreshing(true);
    try {
      const result = await inquiryRepository.getById(id, token);
      if (result.success && result.data?.status === '作業完了') {
        navigate(buildReceiptPath(id, tokenParam), { replace: true });
      }
    } catch {
      // API失敗時は待機表示を継続
    } finally {
      setIsRefreshing(false);
    }
  };

  // PPTX指示(0506) スライド13: ローディング中は「＜」を非表示にする。
  // 戻る操作で多重リクエストが発生したり中断状態に戻れたりするのを防ぐ。
  // WebView 環境では Flutter 側のヘッダ戻るボタンを隠す。
  useEffect(() => {
    if (isWebView) {
      appBridge.hideBackButton();
    }
  }, [isWebView]);

  // 決済完了が確定したら（請求書払いの即完了）Flutter フッターに「アンケートへ進む」ボタンを
  // 表示するよう通知（サイン完了パターン signatureCompleteReady と同じ仕組み）
  useEffect(() => {
    if (isPaymentComplete) {
      postMessageToFlutter('paymentCompleted');
    }
  }, [isPaymentComplete]);

  // アンケートへ進む
  const handleProceedToSurvey = () => {
    navigate(`/inquiries/${id}/survey`, { replace: true });
  };

  return (
    <div ref={contentRef} className="pending-container">
      <main className="pending-content">
        {isPaymentComplete ? (
          <>
            <img src="/assets/images/check-circle.png" alt="完了" className="pending-icon" />
            <h2 className="pending-title">決済手続きが完了しました</h2>
            <p>ご利用ありがとうございました</p>
          </>
        ) : (
          <>
            <div className="pending-icon">
              <CustomLoader size={20} />
            </div>
            <h2 className="pending-title">{title}</h2>
            <p style={{ whiteSpace: 'pre-line' }}>{message}</p>
            <button className="refresh-link" onClick={handleRefresh} disabled={isRefreshing}>
              {isRefreshing ? '確認中...' : '更新する'}
            </button>
          </>
        )}
      </main>

      {/* 決済完了時: アンケートへ進むボタン
          WebView 経由の場合は Flutter ネイティブのフッターに同じボタンが
          表示されるため React 側のボタンは非表示（SignatureComplete と同パターン）。
          Web 直接アクセス時のみ React 側のボタンを表示する。 */}
      {isPaymentComplete && !isWebView && (
        <footer className="pending-footer">
          <button className="button-primary" onClick={handleProceedToSurvey}>
            アンケートへ進む
          </button>
        </footer>
      )}

    </div>
  );
}

export default PaymentPending;
